import React, {FC, useState, useMemo, useEffect} from 'react';
import cn from 'classnames';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {useLocalizer, Interpolation} from '@/common/services/localizer';
import {informerCodeGenerator, informerCssUrlGenerator} from '@/informers/services/informerGenerator/informerGenerator';
import {copyToClipboard} from '@/common/helpers/utils/copyToClipboard';
import {Checkbox} from '@/common/components/Checkbox';
import styles from './CodeResult.css';

interface CodeResultProps {
	scriptUrl: string;
	onClose(): void;
}

export const CodeResult: FC<CodeResultProps> = props => {
	const {scriptUrl, onClose} = props;

	const t = useLocalizer();

	const {state} = useInformerConfigState();
	const [confirmed, setConfirmed] = useState(false);
	const [copiedHintShown, setCopiedHintShown] = useState(false);

	const code = useMemo(() => {
		const layout = informerCodeGenerator(state, true);
		// TODO use DOMAIN_NAME  instead of location.hostname
		const css = `<link rel="stylesheet" href="https://${location.hostname}${informerCssUrlGenerator()}">`;
		const script = `<script src="https://${location.hostname}${scriptUrl}"></script>`;
		return `${css}\n${layout}\n${script}`;
	}, []);

	const handleConfirmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setConfirmed(event.currentTarget.checked);
	};

	const handleCopy = () => {
		copyToClipboard(code);
		setCopiedHintShown(true);
	};

	useEffect(() => {
		if (!copiedHintShown) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			setCopiedHintShown(false);
		}, 3000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [copiedHintShown]);

	const handleHandleSelect = (event: React.MouseEvent<HTMLTextAreaElement>) => {
		const end = event.currentTarget.value.length;
		event.currentTarget.setSelectionRange(0, end);
	};

	return (
		<div className={styles.root}>
			<div className={styles.modal}>
				<div className={styles.title}>{t('resultTitle')}</div>
				<button className={styles.closeButton} type='button' onClick={onClose} />
				<div className={cn(styles.textareaWrapper, !confirmed && styles.isDisabled)}>
					<textarea
						className={styles.textarea}
						disabled={!confirmed}
						value={code}
						onChange={() => {
							/* Do nothing */
						}}
						onClick={confirmed ? handleHandleSelect : undefined}
					/>
				</div>

				<label className={styles.confirmField}>
					<Checkbox className={styles.confirmCheckbox} onChange={handleConfirmChange} />
					<p>
						<Interpolation
							l10nCode='resultConfirm'
							privacypolicy={text => (
								<a className={styles.confirmLink} href='/privacypolicy' target='_blank'>
									{text}
								</a>
							)}
							agreement={text => (
								<a className={styles.confirmLink} href='/agreement' target='_blank'>
									{text}
								</a>
							)}
						/>
					</p>
				</label>
				<div>
					<button className={styles.copyButton} type='button' disabled={!confirmed} onClick={handleCopy}>
						{t('resultCopy')}
					</button>
					<div className={cn(styles.copiedHint, copiedHintShown && styles.isShown)}>{t('resultCopied')}</div>
				</div>
			</div>
		</div>
	);
};

CodeResult.displayName = 'CodeResult';
